/** Party naming and colour, kept out of the component file so fast refresh
 *  still works there. */

import type { Member, MemberSort, WithParty } from "../api";

/** The roster spells parties out; roll calls use one letter. */
export function partyKey(party: string | null): "D" | "R" | "I" {
  if (party === "Democrat" || party === "D") return "D";
  if (party === "Republican" || party === "R") return "R";
  return "I";
}

export const TINT: Record<string, string> = {
  D: "text-dem",
  R: "text-gop",
  I: "text-ind",
};

export const FILL: Record<string, string> = {
  D: "bg-dem",
  R: "bg-gop",
  I: "bg-ind",
};

/** The same three colours again, for a ring around a portrait.
 *
 *  Tailwind draws a ring as an outset shadow, so the colour sits outside
 *  the image rather than over it. The party used to be a bar across the
 *  bottom of the circle, which covered the chin of every member on the
 *  site — these portraits are cropped tight, and a strip across a 56px
 *  circle is a real part of a face. */
export const RING: Record<string, string> = {
  D: "ring-dem",
  R: "ring-gop",
  I: "ring-ind",
};

/** The same three colours as `FILL`, for SVG.
 *
 *  `bg-*` sets a background and an SVG circle has none, so painting a graph
 *  with the `FILL` map above silently produces forty black dots — which is
 *  a legend that says party and a picture that does not. */
export const PAINT: Record<string, string> = {
  D: "fill-dem",
  R: "fill-gop",
  I: "fill-ind",
};

export const PARTY_NAME: Record<string, string> = {
  D: "Democrat",
  R: "Republican",
  I: "Independent",
};

/**
 * The share as a percentage, or a dash when there is nothing to divide.
 *
 * A dash rather than 0%: a member with no roll where their party took a side
 * has not broken with anyone, and 0% on a card is a claim that they always
 * do.
 */
export function loyalty(withParty: WithParty): string {
  return withParty.share === null ? "—" : `${Math.round(withParty.share * 100)}%`;
}

/** How much record it takes before a rate is ranked. Mirrors `_RANKABLE` in
 *  the store; used here only to tell a reader why someone is at the bottom
 *  of a list they might have expected to top. */
export const RANKABLE = 100;

/** The share of rolls they were called on and did not vote. */
export function absence(member: Pick<Member, "votes" | "missed">): string {
  const called = member.votes + member.missed;
  return called ? `${Math.round((member.missed / called) * 100)}%` : "—";
}

/**
 * What each ordering puts on the card, so the figure shown is the figure
 * sorted on. A list ordered by one number while displaying another reads as
 * broken, and the reader is right.
 */
export const SORTS: {
  key: MemberSort;
  label: string;
  stat: (m: Member) => string;
  caption: string;
  /** What that figure is, in a sentence, for the line under the heading. */
  explains: string;
  /** True when thin records are pushed to the end rather than ranked. */
  ranked?: boolean;
}[] = [
  {
    key: "name",
    label: "Name (A–Z)",
    stat: (m) => loyalty(m.with_party),
    caption: "with party",
    explains: "how often they voted with the rest of their own party",
  },
  {
    key: "independent",
    label: "Least loyal to their party",
    stat: (m) => loyalty(m.with_party),
    caption: "with party",
    explains: "how often they voted with the rest of their own party",
    ranked: true,
  },
  {
    key: "loyal",
    label: "Most loyal to their party",
    stat: (m) => loyalty(m.with_party),
    caption: "with party",
    explains: "how often they voted with the rest of their own party",
    ranked: true,
  },
  {
    key: "votes",
    label: "Most votes cast",
    stat: (m) => m.votes.toLocaleString(),
    caption: "votes cast",
    explains: "how many votes they cast",
  },
  {
    key: "absent",
    label: "Missed the most votes",
    stat: absence,
    caption: "did not vote",
    explains: "the share of votes they were called on for and did not cast",
    ranked: true,
  },
  {
    key: "sponsored",
    label: "Most bills sponsored",
    stat: (m) => m.sponsored.toLocaleString(),
    caption: "sponsored",
    explains: "how many bills they put their own name to",
  },
  {
    key: "laws",
    label: "Most bills made law",
    stat: (m) => m.laws.toLocaleString(),
    caption: "became law",
    explains: "how many bills they led on became law",
  },
  {
    key: "lobbied",
    label: "Most money from lobbyists",
    stat: (m) => brief(m.money.lobbied),
    caption: "from lobbyists",
    explains:
      "what registered lobbyists disclosed giving them — a small, precisely named slice of the money, not the whole of it",
  },
  {
    key: "raised",
    label: "Raised the most",
    stat: (m) => (m.money.raised === null ? "—" : brief(m.money.raised)),
    caption: "last cycle",
    explains: "everything their campaign raised in their most recent cycle",
  },
  {
    key: "pac",
    label: "Most PAC-funded",
    stat: (m) =>
      m.money.pac_share === null ? "—" : `${Math.round(m.money.pac_share * 100)}%`,
    caption: "from PACs",
    explains:
      "the share of their last campaign that came from political committees rather than people",
    ranked: true,
  },
];

/** $1.2m, $64k, $500 — comparable at a glance, which is all a card needs. */
/** $50,000,000 rather than $50m. For a row that is the evidence for a
 *  figure rather than the figure itself: a reader checking it against the
 *  sworn filing needs what the filing carries. */
export function exact(value: number): string {
  return `$${Math.round(value).toLocaleString()}`;
}

export function brief(value: number): string {
  if (!value) return "$0";
  // Billions, because the corpus reached one. Without this the money that
  // reached members of Congress renders "$1351m", which a reader has to
  // count the digits of on a page arguing from the size of it.
  if (value >= 1_000_000_000)
    return `$${(value / 1_000_000_000).toFixed(value >= 10_000_000_000 ? 0 : 1)}b`;
  if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(value >= 10_000_000 ? 0 : 1)}m`;
  if (value >= 1_000) return `$${Math.round(value / 1_000)}k`;
  return `$${Math.round(value)}`;
}

export function sortBy(key: string): (typeof SORTS)[number] {
  return SORTS.find((s) => s.key === key) ?? SORTS[0];
}
